(ns arthur.flow.measure.interior
  "Pixel measurement inside the inner lip ring. The image crop is supplied by
  ingestion; this namespace owns Otsu, morphology, component choice and radial
  contour correspondence, and never reads a DOM element."
  (:require [arthur.domain.geom :as geom]))

(defn- scaled-ring [ring amount]
  (let [{:keys [x y]} (geom/centroid ring)]
    (mapv (fn [p] {:x (+ x (* amount (- (:x p) x)))
                   :y (+ y (* amount (- (:y p) y)))}) ring)))

(defn crop
  "A clamped source-pixel box for the raw inner-lip ring. The box is independent
  of teeth settings so its pixels can be reused when those settings change. nil
  means too small for a useful contrast measurement."
  [ring [width height]]
  (let [x0 (max 0 (js/Math.floor (* width (reduce min (map :x ring)))))
        y0 (max 0 (js/Math.floor (* height (reduce min (map :y ring)))))
        x1 (min width (js/Math.ceil (* width (reduce max (map :x ring)))))
        y1 (min height (js/Math.ceil (* height (reduce max (map :y ring)))))
        w (- x1 x0) h (- y1 y0)]
    (when (and (>= w 5) (>= h 5))
      {:x x0 :y y0 :w w :h h :shape ring
       :source-width width :source-height height})))

(defn- point-in-poly? [points x y]
  (let [n (count points)]
    (loop [i 0 j (dec n) inside? false]
      (if (= i n)
        inside?
        (let [a (nth points i) b (nth points j)
              cross? (and (not= (> (:y a) y) (> (:y b) y))
                          (< x (+ (:x a)
                                  (/ (* (- (:x b) (:x a)) (- y (:y a)))
                                     (- (:y b) (:y a))))))]
          (recur (inc i) i (if cross? (not inside?) inside?)))))))

(defn- otsu [hist total]
  (let [sum (reduce + (map-indexed * hist))]
    (loop [t 0 weight 0 sum-dark 0 best-var -1 best {:thr 0 :dark 0 :bright 0}]
      (if (= t 256)
        best
        (let [weight (+ weight (aget hist t))
              sum-dark (+ sum-dark (* t (aget hist t)))
              remain (- total weight)]
          (if (zero? remain)
            best
            (let [dark (if (pos? weight) (/ sum-dark weight) 0)
                  bright (/ (- sum sum-dark) remain)
                  delta (- dark bright)
                  variance (* weight remain delta delta)]
              (recur (inc t) weight sum-dark
                     (max best-var variance)
                     (if (and (pos? weight) (> variance best-var))
                       {:thr t :dark dark :bright bright}
                       best)))))))))

(defn- morph [mask w h dilate?]
  (let [out (js/Uint8Array. (.-length mask))]
    (doseq [y (range 1 (dec h)) x (range 1 (dec w))]
      (let [i (+ (* y w) x)
            values [(aget mask i) (aget mask (dec i)) (aget mask (inc i))
                    (aget mask (- i w)) (aget mask (+ i w))]]
        (aset out i (if (if dilate? (some pos? values) (every? pos? values)) 1 0))))
    out))

(defn- best-component [mask w h top-bias]
  (let [labels (js/Int32Array. (.-length mask))
        stack (array)]
    (.fill labels -1)
    (loop [seed 0 label 0 best nil]
      (if (= seed (.-length mask))
        best
        (if (or (zero? (aget mask seed)) (>= (aget labels seed) 0))
          (recur (inc seed) label best)
          (do
            (set! (.-length stack) 0)
            (.push stack seed)
            (aset labels seed label)
            (let [{:keys [pixels sum-y]}
                  (loop [pixels [] sum-y 0]
                    (if (zero? (.-length stack))
                      {:pixels pixels :sum-y sum-y}
                      (let [i (.pop stack)
                            x (mod i w) y (quot i w)
                            neighbours (cond-> []
                                         (> x 0) (conj (dec i))
                                         (< x (dec w)) (conj (inc i))
                                         (> y 0) (conj (- i w))
                                         (< y (dec h)) (conj (+ i w)))]
                        (doseq [j neighbours]
                          (when (and (pos? (aget mask j)) (neg? (aget labels j)))
                            (aset labels j label)
                            (.push stack j)))
                        (recur (conj pixels i) (+ sum-y y)))))
                  area (count pixels)
                  mean-y (/ sum-y area h)
                  score (* area (- 1 (* top-bias mean-y)))
                  winner {:pixels pixels :area area :score score :mean-y mean-y}]
              (recur (inc seed) (inc label)
                     (if (or (nil? best) (> score (:score best))) winner best)))))))))

(defn- radial-contour [mask w h cx cy vertices]
  (let [maximum (js/Math.hypot w h)]
    (loop [k 0 previous 1 out []]
      (if (= k vertices)
        out
        (let [angle (- (* (/ k vertices) js/Math.PI 2))
              dx (js/Math.cos angle) dy (js/Math.sin angle)
              hit (loop [radius 0.5 last-hit 0]
                    (if (>= radius maximum)
                      last-hit
                      (let [x (js/Math.round (+ cx (* dx radius)))
                            y (js/Math.round (+ cy (* dy radius)))]
                        (if (or (< x 0) (< y 0) (>= x w) (>= y h))
                          last-hit
                          (if (pos? (aget mask (+ (* y w) x)))
                            (recur (+ radius 0.5) radius)
                            (if (and (pos? last-hit) (> radius (+ last-hit 2)))
                              last-hit
                              (recur (+ radius 0.5) last-hit)))))))
              radius (if (pos? hit) hit (* previous 0.6))]
          (recur (inc k) radius
                 (conj out {:x (+ cx (* dx radius))
                            :y (+ cy (* dy radius))})))))))

(defn measure
  "Measure one cropped RGBA ImageData at `box`. Returns normalized source image
  coordinates and optional intermediate masks for a diagnostic view."
  [{:keys [cavity-erode tongue-reject blob-grow top-bias teeth-verts min-area
           debug?]}
   {:keys [x y w h shape source-width source-height] :as box}
   image-data]
  (let [pixels (.-data image-data)
        polygon (mapv (fn [p] {:x (- (* (:x p) source-width) x)
                              :y (- (* (:y p) source-height) y)})
                      (scaled-ring shape (- 1 cavity-erode)))
        hist (js/Uint32Array. 256)
        luminance (js/Uint8Array. (* w h))
        redness (js/Float32Array. (* w h))
        region (js/Uint8Array. (* w h))
        samples (volatile! 0)]
    (doseq [row (range h) col (range w)]
      (when (point-in-poly? polygon (+ col 0.5) (+ row 0.5))
        (let [i (+ (* row w) col) o (* i 4)
              r (aget pixels o) g (aget pixels (inc o)) b (aget pixels (+ o 2))
              lum (js/Math.floor (+ (* 0.299 r) (* 0.587 g) (* 0.114 b)))]
          (aset luminance i lum)
          (aset redness i (/ (- r (/ (+ g b) 2)) 255))
          (aset region i 1)
          (aset hist lum (inc (aget hist lum)))
          (vswap! samples inc))))
    (if (< @samples 24)
      {:contour nil :contrast 0 :area 0 :debug (when debug? {:box box :region region})}
      (let [{:keys [thr dark bright]} (otsu hist @samples)
            contrast (/ (- bright dark) 255)
            ;; Warm light shifts even the pale teeth toward red. An absolute
            ;; tongue-rejection cut can exclude every pixel in a real mouth.
            ;; Calibrate the cut to the least-red bright quarter of THIS cavity,
            ;; while retaining the authored cut as a floor for neutral footage.
            bright-red (->> (range (* w h))
                            (keep (fn [i]
                                    (when (and (pos? (aget region i))
                                               (> (aget luminance i) bright))
                                      (aget redness i))))
                            sort vec)
            red-cut (if (seq bright-red)
                      (max tongue-reject
                           (+ 0.015 (nth bright-red
                                         (js/Math.floor (* 0.25 (dec (count bright-red)))))))
                      tongue-reject)
            candidate (js/Uint8Array. (* w h))]
        (dotimes [i (* w h)]
          (when (and (pos? (aget region i)) (> (aget luminance i) bright)
                     (< (aget redness i) red-cut))
            (aset candidate i 1)))
        (let [opened (morph (morph candidate w h false) w h true)
              adjusted (loop [remaining (abs blob-grow) mask opened]
                         (if (zero? remaining) mask
                           (recur (dec remaining) (morph mask w h (pos? blob-grow)))))
              component (best-component adjusted w h top-bias)
              area (or (:area component) 0)
              selected (js/Uint8Array. (* w h))]
          (when component
            (doseq [i (:pixels component)] (aset selected i 1)))
          (let [contour (when (>= area min-area)
                          (let [cx (/ (reduce + (map #(mod % w) (:pixels component))) area)
                                cy (/ (reduce + (map #(quot % w) (:pixels component))) area)]
                            (mapv (fn [p] {:x (/ (+ (:x p) x) source-width)
                                           :y (/ (+ (:y p) y) source-height)})
                                  (radial-contour selected w h cx cy teeth-verts))))]
            {:contour contour :contrast contrast :area area
             :debug (when debug? {:box box :region region :luminance luminance
                                  :redness redness :candidate candidate
                                  :opened opened :selected selected
                                  :threshold thr :dark dark :bright bright
                                  :red-cut red-cut})}))))))

(defn head-local
  "Map a pixel-derived contour through the same conditioned similarity as the
  lip rings. Both coordinates are normalized by image height first."
  [measure transform aspect]
  (if-let [contour (:contour measure)]
    (assoc measure :contour
           (geom/apply-sim-all transform
                               (mapv (fn [p] (update p :x * aspect)) contour)))
    measure))
